import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useConfirm } from "@hulahoop/ui";
import {
  useNotificationInbox,
  type NotificationReadFilter,
} from "@/application/workflows/notifications";
import type { WorkflowOutcome } from "@/application/workflows/shared/outcome";
import type { Notification } from "@/domain/notification";
import { getNotificationLink } from "@/utils/notificationRouting";

const READ_FILTERS: readonly NotificationReadFilter[] = ["all", "unread", "read"];

const isReadFilter = (value: unknown): value is NotificationReadFilter =>
  READ_FILTERS.some((filter) => filter === value);

export function useShellInbox() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { confirm, dialog } = useConfirm();
  const [filter, setFilter] = useState<NotificationReadFilter>("all");
  const [failed, setFailed] = useState(false);
  const inbox = useNotificationInbox(filter);

  const track = (outcome: WorkflowOutcome) => {
    setFailed(!outcome.ok);
    return outcome;
  };
  const settle = (outcome: WorkflowOutcome, message: string) => {
    if (!outcome.ok) throw outcome.error;
    setFailed(false);
    return message;
  };

  const handleFilterChange = (value: unknown) => {
    if (isReadFilter(value)) setFilter(value);
  };

  const markOpened = async (notification: Notification) => {
    track(await inbox.open(notification));
  };

  const handleOpen = (notification: Notification) => {
    void markOpened(notification);
    const link = getNotificationLink(notification);
    if (link) navigate(link);
  };

  const handleToggleRead = async (notification: Notification) => {
    track(await inbox.toggleRead(notification));
  };

  const handleMarkAll = async () => {
    track(await inbox.markAllRead());
  };

  const handleDelete = (notification: Notification) =>
    confirm({
      title: t("notifications.delete_one_title"),
      description: t("notifications.delete_one_description"),
      actionLabel: t("notifications.delete"),
      destructive: true,
      errorMessage: t("shell.action_failed"),
      run: async () => settle(await inbox.remove(notification), t("notifications.delete_one_done")),
    });

  const handleDeleteAll = () =>
    confirm({
      title: t("notifications.delete_all_title", { count: inbox.stats.data?.total_count ?? 0 }),
      description: t("notifications.delete_all_description"),
      actionLabel: t("notifications.delete"),
      destructive: true,
      errorMessage: t("shell.action_failed"),
      run: async () => settle(await inbox.remove("all"), t("notifications.delete_all_done")),
    });

  return {
    ...inbox,
    filter,
    failed,
    dialog,
    unreadCount: inbox.stats.data?.unread_count ?? 0,
    totalCount: inbox.stats.data?.total_count ?? 0,
    handleFilterChange,
    handleOpen,
    handleToggleRead,
    handleMarkAll,
    handleDelete,
    handleDeleteAll,
  };
}

export type ShellInboxState = ReturnType<typeof useShellInbox>;
